import React, { useState, useEffect, useContext } from 'react';
import { USAContext } from './USAContext';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Alert,
  AlertDescription,
  Badge
} from '../components/ui';
import {
  CheckCircle,
  AlertTriangle,
  Package,
  MapPin,
  DollarSign,
  Utensils,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Upload
} from 'lucide-react';

// Import section components
import OverviewSection from './components/OverviewSection';
import FDASection from './components/FDASection';
import AddressValidationSection from './components/AddressValidationSection';
import PurchaseLabelsSection from './components/PurchaseLabelsSection';

// Utility function to calculate time ago
const getTimeAgo = (timestamp) => {
  const now = new Date();
  const createdDate = new Date(timestamp);
  const diffInSeconds = Math.floor((now - createdDate) / 1000);
  
  if (diffInSeconds < 60) {
    return `${diffInSeconds} seconds ago`;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }
};

const SmartUSADashboard = () => {
  const {
    shipmentId,
    shipmentItems,
    boxes,
    asinData,
    reloadAsinData,
    clientsData,
    plans,
    selectedPlan,
    setSelectedPlan,
    refreshBoxes,
    trackingCodeMap,
    fetchPlanDetails,
    getTrackingCodeForBox,
    shipmentInfo
  } = useContext(USAContext);

  // Step management
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [showPlanSelector, setShowPlanSelector] = useState(true);
  
  // Dashboard state
  const [dashboardData, setDashboardData] = useState({
    isLoading: true,
    validationStatus: {
      planSelected: false,
      trackingCodes: { ready: 0, total: 0 },
      fdaItems: { submitted: 0, total: 0, items: [] },
      addresses: { confirmed: 0, total: 0 },
      readyToPurchase: false
    },
    costEstimate: 0,
    error: null
  });

  const [selectedFoodItems, setSelectedFoodItems] = useState([]);
  const [confirmedAddresses, setConfirmedAddresses] = useState(new Set());
  const [shipmentLabels, setShipmentLabels] = useState([]);
  const [isConvertingLabels, setIsConvertingLabels] = useState(false);

  // Step definitions
  const steps = [
    { id: 0, title: 'Review & Setup', icon: Package },
    { id: 1, title: 'FDA Check', icon: Utensils },
    { id: 2, title: 'Address Validation', icon: MapPin },
    { id: 3, title: 'Purchase Labels', icon: DollarSign }
  ];

  // No automatic plan selection - user chooses manually

  // Load dashboard data when plan is selected (only on initial mount, not when changed via handlePlanSelect)
  useEffect(() => {
    if (selectedPlan && dashboardData.isLoading && !showPlanSelector) {
      // Only load if we haven't loaded yet and plan selector is not showing
      console.log('Loading dashboard data from useEffect for plan:', selectedPlan.id);
      loadDashboardData(selectedPlan);
    } else if (!selectedPlan && dashboardData.isLoading) {
      // Try to load boxes directly if no plan is selected
      console.log('No plan selected, attempting to load boxes directly from useEffect');
      loadDashboardData(null);
    }
  }, [selectedPlan]);

  // Plan selection is now manual only

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    console.log('Plan selected:', plan.id, plan.name);
    setSelectedPlan(plan);
    setShowPlanSelector(false);
    // Reset progress when changing plans
    setActiveStep(0);
    setCompletedSteps(new Set());
    setSelectedFoodItems([]);
    setConfirmedAddresses(new Set());
    
    // Load dashboard data immediately with the selected plan
    loadDashboardData(plan);
  };

  // Helper functions for identifier matching (similar to BoxContext)
  const getIdentifierCache = () => {
    try {
      const cache = localStorage.getItem('asin_fnsku_cache_usa_dashboard');
      return cache ? JSON.parse(cache) : {};
    } catch (error) {
      console.error('Error reading identifier cache:', error);
      return {};
    }
  };

  const updateIdentifierCache = (merchantSku, identifierData) => {
    try {
      const cache = getIdentifierCache();
      cache[merchantSku] = {
        ...identifierData,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('asin_fnsku_cache_usa_dashboard', JSON.stringify(cache));
    } catch (error) {
      console.error('Error updating identifier cache:', error);
    }
  };

  // Function to match missing ASINs and FNSKUs (similar to BoxContext logic)
  const matchMissingIdentifiers = async (items) => {
    if (!items || items.length === 0) return items;

    const cache = getIdentifierCache();
    
    // Check if any items have blank ASINs or FNSKUs
    const itemsWithBlankIdentifiers = items.filter(item => {
      const merchantSku = item.item.merchant_sku;
      // Check if we have valid data in cache
      if (cache[merchantSku]) {
        // Only use cache data if it's better than what we already have
        if (cache[merchantSku].asin && !item.item.asin) {
          item.item.asin = cache[merchantSku].asin;
        }
        if (cache[merchantSku].fnsku && !item.item.fnsku) {
          item.item.fnsku = cache[merchantSku].fnsku;
        }
        
        // Check if we still need to process this item
        return !item.item.asin || !item.item.fnsku;
      }
      
      return !item.item.asin || !item.item.fnsku;
    });

    if (itemsWithBlankIdentifiers.length > 0) {
      console.log(`SmartUSADashboard: Matching ASINs and FNSKUs for ${itemsWithBlankIdentifiers.length} items...`);

      // Get the client's Amazon channel ID
      const teamId = shipmentInfo?.team_id;
      if (!teamId) {
        console.warn('No team ID available for identifier matching');
        return items;
      }
      
      try {
        const channelsResponse = await fetch(`https://app.yyzprep.ca/api/channels?api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`, {
          headers: {
            'x-selected-client-id': teamId,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        const channelsData = await channelsResponse.json();
        const amazonChannel = channelsData.channels?.find(channel => channel.type === 'amazon');
        
        if (amazonChannel) {
          const amazonChannelId = amazonChannel.id;
          
          // Process each item with blank identifiers
          for (const [index, item] of itemsWithBlankIdentifiers.entries()) {
            const merchantSku = item.item.merchant_sku;
            if (merchantSku) {
              console.log(`SmartUSADashboard: Processing item ${index + 1} of ${itemsWithBlankIdentifiers.length}: ${merchantSku}`);
              
              const listingsUrl = `https://app.yyzprep.ca/api/channels/${amazonChannelId}/listings?page=1&per_page=20&q=sku%7E%22${merchantSku}%22+or+channel_identifier%7E%22${merchantSku}%22+or+title%7E%22${merchantSku}%22+or+identifiers.identifier%7E%22${merchantSku}%22&api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`;
              
              const listingsResponse = await fetch(listingsUrl, {
                headers: {
                  'Accept': 'application/json',
                  'x-service-provider-id': '7',
                  'x-selected-client-id': teamId
                }
              });
              const listingsData = await listingsResponse.json();
              
              if (listingsData.data && listingsData.data.length > 0) {
                const listing = listingsData.data.find(listing => listing.sku === merchantSku);
                
                if (listing) {
                  const asinIdentifier = listing.identifiers?.find(id => id.type === 'ASIN');
                  const fnskuIdentifier = listing.identifiers?.find(id => id.type === 'FNSKU');
                  
                  const identifierData = {
                    asin: asinIdentifier?.identifier || null,
                    fnsku: fnskuIdentifier?.identifier || null
                  };
                  
                  // Update item and cache if we found either identifier
                  if (identifierData.asin || identifierData.fnsku) {
                    if (identifierData.asin) item.item.asin = identifierData.asin;
                    if (identifierData.fnsku) item.item.fnsku = identifierData.fnsku;
                    updateIdentifierCache(merchantSku, identifierData);
                    console.log(`SmartUSADashboard: Found identifiers for ${merchantSku}:`, identifierData);
                  }
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('SmartUSADashboard: Error matching identifiers:', error);
      }
    }

    return items;
  };

  // Load all data in parallel
  const loadDashboardData = async (planToLoad = selectedPlan) => {
    if (!planToLoad) {
      console.log('No plan selected, attempting to load boxes via outbound endpoint');
      // Try to load boxes directly using outbound endpoint when no plan is selected
      setDashboardData(prev => ({ ...prev, isLoading: true, error: null }));
      
      try {
        const boxesData = await refreshBoxes(null); // This will try outbound endpoint
        
        const validationStatus = calculateValidationStatus(boxesData, []);
        const costEstimate = calculateCostEstimate(boxesData);

        setDashboardData({
          isLoading: false,
          validationStatus,
          costEstimate,
          error: null
        });

        console.log('Dashboard data loaded without plan:', {
          boxes: boxesData.length,
          costEstimate
        });
        
        return;
      } catch (error) {
        console.error('Failed to load dashboard without plan:', error);
        setDashboardData(prev => ({
          ...prev,
          isLoading: false,
          error: 'No plan selected and unable to load boxes directly'
        }));
        return;
      }
    }
    
    setDashboardData(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      console.log('Loading dashboard data for plan:', planToLoad.id);
      
      // Ensure we have the plan set in context before refreshing boxes
      if (planToLoad !== selectedPlan) {
        setSelectedPlan(planToLoad);
      }

      // Match missing identifiers for items if shipmentItems are available
      let processedItems = shipmentItems;
      if (shipmentItems && shipmentItems.length > 0) {
        console.log('SmartUSADashboard: Checking for missing ASINs/FNSKUs...');
        processedItems = await matchMissingIdentifiers([...shipmentItems]);
      }

      // Preload ASIN data if shipmentItems are available
      if (processedItems && processedItems.length > 0) {
        const asinList = processedItems.map(item => item.item.asin).filter(asin => asin);
        
        if (asinList.length > 0) {
          try {
            console.log(`Auto-refreshing prices for ${asinList.length} ASINs in SmartUSADashboard...`);
            const priceRefreshResponse = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/api/refresh-prices`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ 
                asins: asinList,
                employee: 'SmartUSADashboard-Auto' 
              })
            });
            
            if (priceRefreshResponse.ok) {
              const priceRefreshResult = await priceRefreshResponse.json();
              console.log('Price refresh completed in SmartUSADashboard:', priceRefreshResult.summary);
            }
          } catch (error) {
            console.error('Price refresh failed (non-critical) in SmartUSADashboard:', error);
          }

          // Reload ASIN data to ensure it's fresh
          try {
            await reloadAsinData();
          } catch (error) {
            console.error('ASIN data reload failed (non-critical) in SmartUSADashboard:', error);
          }
        }
      }

      // Parallel data loading - now that we pass the plan directly, no retry needed
      const [boxesData, trackingData, labelsData] = await Promise.all([
        refreshBoxes(planToLoad),
        fetchPlanDetails(planToLoad.id),
        fetchShipmentLabels(planToLoad)
      ]);

      // Calculate validation status
      const validationStatus = calculateValidationStatus(boxesData, labelsData);
      
      // Calculate cost estimate
      const costEstimate = calculateCostEstimate(boxesData);

      setDashboardData({
        isLoading: false,
        validationStatus,
        costEstimate,
        error: null
      });

      setShipmentLabels(labelsData || []);

      console.log('Dashboard data loaded successfully:', {
        boxes: boxesData.length,
        labels: labelsData.length,
        costEstimate
      });

    } catch (error) {
      console.error('Dashboard load failed:', error);
      setDashboardData(prev => ({
        ...prev,
        isLoading: false,
        error: error.message
      }));
    }
  };

  // Fetch shipment labels (no auto-print)
  const fetchShipmentLabels = async (plan) => {
    if (!plan?.placement_options) return [];

    const confirmedPlacement = plan.placement_options.find(option => option.confirmed_at);
    if (!confirmedPlacement) return [];

    const shipmentLabels = [];
    
    for (const shipment of confirmedPlacement.shipments) {
      try {
        const response = await fetch(
          `https://app.yyzprep.ca/api/fba-transport/v2024/plans/${plan.id}/labels?shipment_id=${shipment.shipment_id}&api_token=Yh7l5CUTaZ1nIgAueWglafvm616hchHFFZxRjKjPHNBjB19b2jTDgGoCSpeq`
        );
        
        if (!response.ok) continue;
        
        const data = await response.json();
        
        // Find the best label (4" x 6" preferred, then 8.5" x 11")
        let label = data.data.find(label => 
          label.name.includes('4" x 6"') && label.type === 'package'
        );
        
        if (!label) {
          label = data.data.find(label => 
            label.name.includes('8.5') && label.type === 'package'
          );
        }

        if (label) {
          shipmentLabels.push({
            shipmentId: shipment.shipment_id,
            url: label.url,
            address: shipment.destination.address,
            warehouseId: shipment.destination.warehouse_id
          });
        }
      } catch (error) {
        console.error(`Error fetching labels for shipment ${shipment.shipment_id}:`, error);
      }
    }

    return shipmentLabels;
  };

  // Calculate validation status
  const calculateValidationStatus = (boxesData = [], labelsData = []) => {
    const trackingCodesReady = Object.keys(trackingCodeMap).length;
    const totalBoxes = boxesData.length;
    
    return {
      planSelected: !!selectedPlan,
      trackingCodes: { ready: trackingCodesReady, total: totalBoxes },
      fdaItems: { 
        submitted: 0,
        total: selectedFoodItems.length,
        items: selectedFoodItems 
      },
      addresses: { 
        confirmed: confirmedAddresses.size, 
        total: labelsData.length 
      },
      readyToPurchase: trackingCodesReady === totalBoxes && 
                      confirmedAddresses.size === labelsData.length &&
                      completedSteps.has(1) // FDA step completed
    };
  };

  // Calculate cost estimate
  const calculateCostEstimate = (boxesData = []) => {
    return boxesData.reduce((total, box) => {
      const weightLbs = box.weight * 2.20462;
      let cost = 0;
      
      if (weightLbs < 10) cost = 3.99;
      else if (weightLbs < 20) cost = 5.99;
      else if (weightLbs < 40) cost = 9.99;
      else cost = 13.99;
      
      return total + cost;
    }, 0);
  };

  // Handle food item selection
  const handleFoodItemToggle = (item) => {
    setSelectedFoodItems(prev => {
      const isSelected = prev.some(selected => selected.id === item.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  // Handle address confirmation
  const handleAddressConfirm = (shipmentId) => {
    setConfirmedAddresses(prev => new Set([...prev, shipmentId]));
  };

  // Handle step completion
  const handleStepComplete = (stepId) => {
    setCompletedSteps(prev => new Set([...prev, stepId]));
    
    // Don't auto-advance - let user navigate manually
  };

  // Handle step navigation
  const handleStepClick = (stepId) => {
    // Allow navigation to any step that's accessible
    if (isStepClickable(stepId)) {
      setActiveStep(stepId);
    }
  };

  // Check if step is accessible
  const isStepAccessible = (stepId) => {
    if (stepId === 0) return true;
    if (stepId === 1) return completedSteps.has(0);
    if (stepId === 2) return completedSteps.has(1) && shipmentLabels.length > 0;
    if (stepId === 3) return completedSteps.has(2) && confirmedAddresses.size === shipmentLabels.length;
    return false;
  };

  // Check if step is clickable (can navigate to it)
  const isStepClickable = (stepId) => {
    if (stepId === 0) return true; // Always can go to overview
    if (completedSteps.has(stepId)) return true; // Can go back to completed steps
    if (stepId === activeStep + 1 && isStepAccessible(stepId)) return true; // Can go forward one step if accessible
    return false;
  };

  // Handle prior notice label conversion to 4x6
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsConvertingLabels(true);

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        const payload = {
          name: file.name,
          type: file.type,
          base64Data: base64Data,
          sizeBytes: file.size,
        };

        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL}/proxy-retool`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });

          if (response.ok) {
            const result = await response.json();
            const base64PDF = result.response;

            // Convert base64 PDF back to Blob and download it
            const byteCharacters = atob(base64PDF);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const downloadUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `PN_${selectedPlan?.inbound_plan_id || selectedPlan?.id || 'UNKNOWN'}.pdf`;
            link.click();
          } else {
            console.error('Error processing PDF:', response.statusText);
          }
        } catch (error) {
          console.error('Error uploading file:', error);
        } finally {
          setIsConvertingLabels(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Update validation status when dependencies change
  useEffect(() => {
    if (!dashboardData.isLoading) {
      const newValidationStatus = calculateValidationStatus(boxes, shipmentLabels);
      setDashboardData(prev => ({
        ...prev,
        validationStatus: newValidationStatus
      }));
    }
  }, [boxes, shipmentLabels, selectedFoodItems, confirmedAddresses, trackingCodeMap, completedSteps]);

  if (dashboardData.isLoading && selectedPlan) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <h2 className="text-xl font-semibold text-slate-900">Loading USA Helper</h2>
          <p className="text-slate-500">Fetching plans, boxes, and tracking codes</p>
        </div>
      </div>
    );
  }

  if (dashboardData.error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-2xl mx-auto mt-12">
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">
              <strong>Error loading dashboard:</strong> {dashboardData.error}
              <Button 
                onClick={loadDashboardData} 
                variant="outline" 
                size="sm" 
                className="ml-4"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-3 py-3">
        
        {/* Header */}
        <div className="bg-white rounded shadow-sm border border-slate-200 mb-3">
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-lg font-semibold text-slate-900">USA Helper</h1>
                <div className="flex items-center space-x-2 mt-0.5">
                  {selectedPlan ? (
                    <>
                      <button
                        onClick={() => setShowPlanSelector(!showPlanSelector)}
                        className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-700"
                      >
                        <span>{selectedPlan.name}</span>
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      <Badge variant="outline" className="text-xs px-1 py-0">
                        {selectedPlan.status}
                      </Badge>
                    </>
                  ) : (
                    <button
                      onClick={() => setShowPlanSelector(true)}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Click to select a plan
                    </button>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                {selectedPlan && (
                  <div className="text-right">
                    <div className="flex items-center space-x-1">
                      <DollarSign className="w-3 h-3 text-green-600" />
                      <span className="text-sm font-semibold text-green-600">
                        ${dashboardData.costEstimate.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">Est. Cost</p>
                  </div>
                )}
                
                {/* Convert Labels Button - Always Visible */}
                <div className="relative">
                  <Button
                    variant="outline"
                    disabled={isConvertingLabels}
                    className="h-7 px-3 text-xs"
                    title="Convert Prior Notice Labels to 4x6"
                    onClick={() => document.getElementById('convert-labels-input').click()}
                  >
                    {isConvertingLabels ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-3 h-3 mr-1" />
                        Convert PN Labels
                      </>
                    )}
                  </Button>
                  <input
                    id="convert-labels-input"
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>
                
                <Button 
                  onClick={() => {
                    console.log('Manual refresh clicked for plan:', selectedPlan?.id);
                    loadDashboardData(selectedPlan);
                  }} 
                  variant="outline" 
                  size="sm" 
                  className="h-7 w-7 p-0"
                  title="Refresh dashboard data"
                >
                  <RefreshCw className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {/* Plan Selector Dropdown */}
            {(showPlanSelector || !selectedPlan) && (
              <div className="mt-3 p-3 bg-slate-50 rounded border border-slate-200">
                <h3 className="text-xs font-medium text-slate-900 mb-2">Select Plan:</h3>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {plans.length > 0 ? (
                    plans
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .map((plan, index) => {
                        const createdDate = new Date(plan.created_at);
                        const timeAgo = getTimeAgo(plan.created_at);
                        const isNewest = index === 0; // Most recent plan after sorting
                        
                        // Calculate SKU and unit counts
                        const skuCount = plan.items ? plan.items.length : 0;
                        const unitCount = plan.items ? plan.items.reduce((sum, item) => sum + (item.quantity || 0), 0) : 0;
                        
                        return (
                          <button
                            key={plan.id}
                            onClick={() => handlePlanSelect(plan)}
                            className={`w-full text-left p-2 rounded border transition-colors ${
                              selectedPlan?.id === plan.id
                                ? 'border-blue-300 bg-blue-50'
                                : isNewest
                                  ? 'border-green-400 bg-green-50 hover:bg-green-100'
                                  : 'border-slate-200 bg-white hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <p className="text-xs font-medium text-slate-900 truncate">{plan.name}</p>
                                  {isNewest && (
                                    <Badge variant="outline" className="text-xs px-1 py-0 bg-green-100 text-green-700 border-green-300">
                                      Most Recent
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center space-x-1 mt-0.5">
                                  <p className="text-xs text-slate-500">ID: {plan.inbound_plan_id || plan.id}</p>
                                  <span className="text-xs text-slate-300">•</span>
                                  <p className="text-xs text-slate-500">{skuCount} SKUs</p>
                                  <span className="text-xs text-slate-300">•</span>
                                  <p className="text-xs text-slate-500">{unitCount} units</p>
                                </div>
                                <div className="flex items-center space-x-1 mt-0.5">
                                  <p className="text-xs text-slate-400">{timeAgo}</p>
                                </div>
                              </div>
                              <div className="ml-2 flex-shrink-0">
                                <Badge 
                                  variant={plan.status === 'SHIPPED' ? 'default' : 'secondary'}
                                  className="text-xs px-1 py-0"
                                >
                                  {plan.status}
                                </Badge>
                              </div>
                            </div>
                          </button>
                        );
                      })
                  ) : (
                    <p className="text-xs text-slate-500">No plans available</p>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Step Navigation */}
          {selectedPlan && (
            <div className="px-4 py-2">
              <div className="flex items-center justify-between">
                {steps.map((step, index) => {
                  const isActive = index === activeStep;
                  const isCompleted = completedSteps.has(index);
                  const isAccessible = isStepAccessible(index);
                  const isClickable = isStepClickable(index);
                  const Icon = step.icon;

                  return (
                    <div key={step.id} className="flex items-center">
                      <button
                        onClick={() => handleStepClick(index)}
                        disabled={!isClickable}
                        className={`flex items-center space-x-2 px-2 py-1.5 rounded transition-colors ${
                          isActive 
                            ? 'bg-blue-50 text-blue-700' 
                            : isCompleted
                              ? 'bg-green-50 text-green-700 hover:bg-green-100'
                              : isClickable
                                ? 'text-slate-600 hover:bg-slate-50'
                                : 'text-slate-400 cursor-not-allowed'
                        }`}
                      >
                        <div className={`flex items-center justify-center w-6 h-6 rounded-full border ${
                          isActive 
                            ? 'bg-blue-500 border-blue-500 text-white' 
                            : isCompleted
                              ? 'bg-green-500 border-green-500 text-white'
                              : isClickable
                                ? 'bg-white border-slate-300 text-slate-500'
                                : 'bg-slate-100 border-slate-200 text-slate-400'
                        }`}>
                          {isCompleted ? (
                            <CheckCircle className="w-3 h-3" />
                          ) : (
                            <span className="text-xs font-medium">{index + 1}</span>
                          )}
                        </div>
                        <div className="text-left">
                          <p className={`text-xs font-medium ${
                            isActive ? 'text-blue-700' : isCompleted ? 'text-green-700' : 'text-slate-600'
                          }`}>
                            {step.title}
                          </p>
                        </div>
                      </button>
                      {index < steps.length - 1 && (
                        <div className={`flex-1 mx-2 h-0.5 ${
                          completedSteps.has(index) ? 'bg-green-500' : 'bg-slate-300'
                        }`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Step Content */}
        {selectedPlan ? (
          <div className="bg-white rounded shadow-sm border border-slate-200">
            <div className="p-4">
              {activeStep === 0 && (
                <OverviewSection 
                  validationStatus={dashboardData.validationStatus}
                  boxes={boxes}
                  trackingCodeMap={trackingCodeMap}
                  selectedPlan={selectedPlan}
                  shipmentItems={shipmentItems}
                  asinData={asinData}
                  reloadAsinData={reloadAsinData}
                  refreshBoxes={refreshBoxes}
                  onComplete={() => handleStepComplete(0)}
                />
              )}
              
              {activeStep === 1 && (
                <FDASection 
                  shipmentId={shipmentId}
                  shipmentItems={shipmentItems}
                  selectedItems={selectedFoodItems}
                  onItemToggle={handleFoodItemToggle}
                  onComplete={() => handleStepComplete(1)}
                  clientsData={clientsData}
                  shipmentInfo={shipmentInfo}
                  boxes={boxes}
                  selectedPlan={selectedPlan}
                />
              )}
              
              {activeStep === 2 && (
                <AddressValidationSection 
                  shipmentLabels={shipmentLabels}
                  confirmedAddresses={confirmedAddresses}
                  onAddressConfirm={handleAddressConfirm}
                  onComplete={() => handleStepComplete(2)}
                />
              )}
              
              {activeStep === 3 && (
                <PurchaseLabelsSection 
                  boxes={boxes}
                  costEstimate={dashboardData.costEstimate}
                  trackingCodeMap={trackingCodeMap}
                  validationStatus={dashboardData.validationStatus}
                  selectedPlan={selectedPlan}
                  clientsData={clientsData}
                  asinData={asinData}
                  shipmentInfo={shipmentInfo}
                  shipmentLabels={shipmentLabels}
                  confirmedAddresses={confirmedAddresses}
                  onComplete={() => handleStepComplete(3)}
                />
              )}
            </div>

            {/* Navigation Footer */}
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                disabled={activeStep === 0}
                className="flex items-center space-x-1 h-7 text-xs"
              >
                <ChevronLeft className="w-3 h-3" />
                <span>Back</span>
              </Button>
              
              <div className="text-xs text-slate-500">
                Step {activeStep + 1} of {steps.length}
              </div>
              
              <Button
                onClick={() => {
                  const nextStep = activeStep + 1;
                  if (nextStep < steps.length && isStepClickable(nextStep)) {
                    setActiveStep(nextStep);
                  }
                }}
                disabled={!isStepClickable(activeStep + 1)}
                className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 h-7 text-xs"
              >
                <span>{activeStep === steps.length - 1 ? 'Complete' : 'Next'}</span>
                <ChevronRight className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded shadow-sm border border-slate-200 p-8 text-center">
            <Package className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <h3 className="text-base font-medium text-slate-900 mb-2">Select a Plan to Continue</h3>
            {boxes && boxes.length > 0 ? (
              <div>
                <p className="text-sm text-slate-500 mb-2">
                  Found {boxes.length} boxes for this shipment. Select a plan to access all features.
                </p>
                <div className="text-xs text-slate-400 mb-3">
                  Some features like tracking codes and label purchase require a plan selection.
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 mb-3">
                Choose a shipment plan to begin processing your USA shipment.
              </p>
            )}
            <Button onClick={() => setShowPlanSelector(true)} className="bg-blue-600 hover:bg-blue-700 h-8 text-xs">
              Browse Available Plans
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmartUSADashboard; 